import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { PropertyCard } from "@/components/PropertyCard";
import { api, money, pct, formatApiError } from "@/lib/api";
import { useDisplayCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck, TrendingUp, Coins, Building2, FileCheck2, Users2, Wallet, Calculator, MapPin } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85";

export function Home() {
  const [properties, setProperties] = useState([]);
  const [metrics, setMetrics] = useState({ funded: 82_400_000, avgYield: 8.9, investors: 12_400, paid: 6_800_000 });
  useEffect(() => { api.get("/properties").then((r) => setProperties(r.data)).catch(() => {}); }, []);
  return (
    <PublicLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Luxury estate" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B192C]/95 via-[#0B192C]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-28 lg:pb-40 text-[#F3EFEA]">
          <div className="max-w-2xl">
            <div className="overline text-[#C5A059] mb-6">Institutional Fractional Real Estate</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Own a fraction of <em className="italic text-[#C5A059]">landmark</em> estates.
            </h1>
            <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-xl">
              From <span className="font-mono">$1,000</span>, invest in curated, income-producing luxury properties with assured monthly rentals and full escrow protection.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/marketplace">
                <Button className="btn-gold rounded-full px-8 h-12 text-base" data-testid="hero-explore-btn">
                  Explore Marketplace<ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/calculator">
                <Button variant="outline" className="rounded-full px-8 h-12 text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0B192C]" data-testid="hero-calc-btn">
                  Yield Calculator
                </Button>
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
              {[
                { l: "Funded", v: money(metrics.funded) },
                { l: "Avg Yield", v: pct(metrics.avgYield, 1) },
                { l: "Investors", v: metrics.investors.toLocaleString() },
                { l: "Distributed", v: money(metrics.paid) },
              ].map((m) => (
                <div key={m.l} className="border-t border-white/20 pt-3">
                  <div className="overline text-[#C5A059]">{m.l}</div>
                  <div className="metric-value text-2xl mt-1 text-white">{m.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <div className="overline">Featured Estates</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#0B192C]">Curated income properties.</h2>
          </div>
          <Link to="/marketplace" className="text-[#0B192C] font-medium gold-underline" data-testid="view-all-properties">View all →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.slice(0, 3).map((p) => <PropertyCard key={p.id} p={p} />)}
        </div>
      </section>

      <section className="bg-[#F3EFEA] border-y border-[#E5DFD5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="overline">How It Works</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#0B192C] max-w-2xl">Four steps to owning premium real estate.</h2>
          <div className="grid md:grid-cols-4 gap-6 mt-14">
            {[
              { i: Building2, t: "Choose Property", d: "Browse vetted, institutional-grade properties with full financials." },
              { i: FileCheck2, t: "Complete KYC", d: "Verify identity in minutes with our compliance flow." },
              { i: Coins, t: "Invest Fractionally", d: "Pick your ticket size from $1,000 and receive a legal share via SPV." },
              { i: Wallet, t: "Earn Monthly", d: "Assured rentals plus capital appreciation, paid directly to your wallet." },
            ].map((s, i) => (
              <div key={s.t} className="bg-white border border-[#E5DFD5] p-7 rounded-xl">
                <div className="h-10 w-10 rounded-lg bg-[#0B192C] flex items-center justify-center">
                  <s.i className="h-5 w-5 text-[#C5A059]" />
                </div>
                <div className="mt-5 overline">Step {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-2xl mt-2 text-[#0B192C]">{s.t}</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="overline">Assured Rentals & Compliance</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#0B192C]">Built like an institution. Priced for individuals.</h2>
          <ul className="mt-8 space-y-4">
            {[
              { i: ShieldCheck, t: "Escrow-protected SPV", d: "Every property is held in a dedicated legal entity with escrow-backed capital." },
              { i: TrendingUp, t: "Assured monthly yield", d: "Contractual rentals guaranteed by long-term triple-net or hospitality lease." },
              { i: Users2, t: "Referral commissions", d: "Earn 5% commission on every dollar your invited investors deploy." },
            ].map((x) => (
              <li key={x.t} className="flex gap-4">
                <div className="h-11 w-11 rounded-lg bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
                  <x.i className="h-5 w-5 text-[#8B6B2E]" />
                </div>
                <div>
                  <div className="font-medium text-[#0B192C]">{x.t}</div>
                  <div className="text-sm text-slate-600 leading-relaxed">{x.d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <YieldCalculatorMini />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E5DFD5]">
        <div className="overline">Frequently asked</div>
        <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#0B192C] mb-10">Investor questions, answered.</h2>
        <Accordion type="single" collapsible className="max-w-3xl">
          {[
            { q: "What's the minimum investment?", a: "You can start owning a fraction from $1,000 per unit. Some flagship properties offer smaller tickets." },
            { q: "How is my capital protected?", a: "Each property is held in a dedicated Special Purpose Vehicle (SPV) with escrow-protected capital and title insurance." },
            { q: "How are rental payouts made?", a: "Monthly or quarterly distributions land directly in your OwnNest wallet, withdrawable to your bank at any time." },
            { q: "Can I exit early?", a: "Secondary marketplace liquidity is available after a 12-month lockup, subject to market matching." },
          ].map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </PublicLayout>
  );
}

function YieldCalculatorMini() {
  const [amount, setAmount] = useState(25000);
  const [yieldPct, setYieldPct] = useState(9);
  const monthly = (amount * yieldPct / 100) / 12;
  const fiveYear = amount + amount * (yieldPct / 100) * 5 + amount * 0.07 * 5;
  return (
    <div className="bg-[#0B192C] text-white rounded-2xl p-8 relative overflow-hidden">
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#C5A059]/10 blur-3xl" />
      <div className="overline text-[#C5A059] relative">Live Calculator</div>
      <h3 className="font-serif text-3xl mt-2 relative">Project your returns.</h3>
      <div className="mt-6 space-y-6 relative">
        <div>
          <div className="flex justify-between mb-2">
            <Label className="text-white/70">Investment amount</Label>
            <span className="font-mono">{money(amount)}</span>
          </div>
          <Slider value={[amount]} min={1000} max={500000} step={1000} onValueChange={(v) => setAmount(v[0])} data-testid="calc-amount-slider" />
        </div>
        <div>
          <div className="flex justify-between mb-2">
            <Label className="text-white/70">Assured yield</Label>
            <span className="font-mono">{yieldPct}%</span>
          </div>
          <Slider value={[yieldPct]} min={6} max={12} step={0.1} onValueChange={(v) => setYieldPct(v[0])} data-testid="calc-yield-slider" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-8 relative">
        <div className="border border-white/15 rounded-xl p-5">
          <div className="overline text-[#C5A059]">Monthly Rental</div>
          <div className="metric-value text-3xl mt-2" data-testid="calc-monthly">{money(monthly)}</div>
        </div>
        <div className="border border-white/15 rounded-xl p-5">
          <div className="overline text-[#C5A059]">5-Yr Value</div>
          <div className="metric-value text-3xl mt-2" data-testid="calc-5yr">{money(fiveYear)}</div>
        </div>
      </div>
    </div>
  );
}

export function Marketplace() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/properties").then((r) => setItems(r.data)); }, []);
  const filtered = useMemo(() => items.filter((p) => !q || (p.title + p.city + p.location).toLowerCase().includes(q.toLowerCase())), [items, q]);
  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline">Marketplace</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-3 text-[#0B192C]">Curated income properties.</h1>
        <div className="mt-8 max-w-md">
          <Input placeholder="Search by city, name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="marketplace-search" />
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((p) => <PropertyCard key={p.id} p={p} />)}
        </div>
        {filtered.length === 0 && <div className="text-center py-20 text-slate-500">No properties found.</div>}
      </div>
    </PublicLayout>
  );
}

export function PropertyDetail() {
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState(1);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency: displayCur, convert } = useDisplayCurrency();
  const id = window.location.pathname.split("/").pop();
  useEffect(() => { api.get(`/properties/${id}`).then((r) => setProperty(r.data)); }, [id]);
  if (!property) return <PublicLayout><div className="max-w-7xl mx-auto py-20 px-6">Loading…</div></PublicLayout>;
  const native = (property.currency || "INR").toUpperCase();
  const amount = units * property.price_per_unit;
  const ownership = (units / property.total_units) * 100;
  const monthly = (amount * property.assured_rental_yield / 100) / 12;
  const isConverted = native !== displayCur;

  const invest = async () => {
    if (!user || !user.user_id) { toast.error("Please sign in to invest"); navigate("/login"); return; }
    if (user.kyc_status !== "approved") { toast.error("Complete KYC before investing"); navigate("/kyc"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { property_id: id, units, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-[#F3EFEA]">
              <img src={property.hero_image} alt={property.title} className="w-full h-full object-cover" />
            </div>
            {property.gallery && property.gallery.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {property.gallery.slice(0, 3).map((g, i) => (
                  <img key={i} src={g} alt="" className="rounded-xl aspect-square object-cover" />
                ))}
              </div>
            )}
            <div>
              <div className="overline">{property.property_type}</div>
              <h1 className="font-serif text-4xl md:text-5xl text-[#0B192C] mt-2">{property.title}</h1>
              <div className="text-slate-600 flex items-center gap-1 mt-2"><MapPin className="h-4 w-4" />{property.location}</div>
              <p className="mt-6 text-slate-700 leading-relaxed">{property.description}</p>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { l: "Total Value", v: money(convert(property.total_value, native), displayCur) },
                { l: "Assured Yield", v: pct(property.assured_rental_yield, 1) },
                { l: "Lease Term", v: `${property.lease_term_years} yrs` },
                { l: "Occupancy", v: property.occupancy_status },
              ].map((x) => (
                <div key={x.l} className="border border-[#E5DFD5] rounded-xl p-5 bg-white">
                  <div className="overline">{x.l}</div>
                  <div className="metric-value text-xl mt-1 text-[#0B192C]">{x.v}</div>
                </div>
              ))}
            </div>
            <Tabs defaultValue="amenities" className="mt-6">
              <TabsList>
                <TabsTrigger value="amenities" data-testid="tab-amenities">Amenities</TabsTrigger>
                <TabsTrigger value="highlights" data-testid="tab-highlights">Highlights</TabsTrigger>
              </TabsList>
              <TabsContent value="amenities">
                <ul className="grid grid-cols-2 gap-3 mt-4">
                  {property.amenities?.map((a) => <li key={a} className="flex items-center gap-2 text-slate-700"><span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />{a}</li>)}
                </ul>
              </TabsContent>
              <TabsContent value="highlights">
                <ul className="space-y-3 mt-4">
                  {property.highlights?.map((h) => <li key={h} className="flex items-start gap-2 text-slate-700"><ShieldCheck className="h-4 w-4 text-[#0F5132] flex-shrink-0 mt-0.5" />{h}</li>)}
                </ul>
              </TabsContent>
            </Tabs>
          </div>
          <div>
            <div className="sticky top-28 bg-[#0B192C] text-white rounded-2xl p-7">
              <div className="overline text-[#C5A059]">Invest Now</div>
              <div className="mt-2 font-serif text-3xl">{money(convert(property.price_per_unit, native), displayCur)}<span className="text-sm text-white/70 font-sans"> / unit</span></div>
              {isConverted && <div className="text-xs font-mono text-white/50 mt-1">≈ {money(property.price_per_unit, native)} native</div>}
              <div className="mt-6">
                <div className="flex justify-between mb-2">
                  <Label className="text-white/70">Units</Label>
                  <span className="font-mono">{units}</span>
                </div>
                <Slider value={[units]} min={1} max={Math.max(1, property.total_units - property.units_sold)} step={1} onValueChange={(v) => setUnits(v[0])} data-testid="pd-units-slider" />
              </div>
              <div className="space-y-3 mt-6 text-sm">
                <div className="flex justify-between"><span className="text-white/70">Investment</span><span className="font-mono">{money(convert(amount, native), displayCur)}</span></div>
                <div className="flex justify-between"><span className="text-white/70">Ownership</span><span className="font-mono">{pct(ownership, 4)}</span></div>
                <div className="flex justify-between"><span className="text-white/70">Monthly Rental</span><span className="font-mono text-[#C5A059]">{money(convert(monthly, native), displayCur)}</span></div>
                <div className="flex justify-between text-xs pt-2 border-t border-white/10"><span className="text-white/60">Settled in</span><span className="font-mono text-white/80">{native}{isConverted ? ` (${money(amount, native)})` : ""}</span></div>
              </div>
              <Button className="w-full mt-6 btn-gold h-12 rounded-full" onClick={invest} disabled={loading} data-testid="pd-invest-btn">
                {loading ? "Processing…" : "Invest Now"}
              </Button>
              <div className="mt-4 text-xs text-white/60 text-center">Escrow-protected · SPV-backed · Fully insured</div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export function CalculatorPage() {
  const [amount, setAmount] = useState(50000);
  const [y, setY] = useState(9);
  const [years, setYears] = useState(5);
  const monthly = (amount * y / 100) / 12;
  const totalRental = monthly * 12 * years;
  const projected = amount + totalRental + amount * 0.07 * years;
  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline">Fractional Ownership Calculator</div>
        <h1 className="font-serif text-5xl mt-3 text-[#0B192C]">Model your returns.</h1>
        <div className="mt-10 bg-white border border-[#E5DFD5] rounded-2xl p-8 space-y-8">
          <div>
            <div className="flex justify-between mb-2"><Label>Investment</Label><span className="font-mono">{money(amount)}</span></div>
            <Slider value={[amount]} min={1000} max={1000000} step={1000} onValueChange={(v) => setAmount(v[0])} data-testid="page-calc-amount" />
          </div>
          <div>
            <div className="flex justify-between mb-2"><Label>Annual Yield</Label><span className="font-mono">{y}%</span></div>
            <Slider value={[y]} min={6} max={12} step={0.1} onValueChange={(v) => setY(v[0])} data-testid="page-calc-yield" />
          </div>
          <div>
            <div className="flex justify-between mb-2"><Label>Horizon</Label><span className="font-mono">{years} yrs</span></div>
            <Slider value={[years]} min={1} max={10} step={1} onValueChange={(v) => setYears(v[0])} data-testid="page-calc-years" />
          </div>
          <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-[#E5DFD5]">
            <Metric label="Monthly Rental" v={money(monthly)} />
            <Metric label="Total Rentals" v={money(totalRental)} />
            <Metric label="Projected Value" v={money(projected)} />
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
function Metric({ label, v }) { return <div><div className="overline">{label}</div><div className="metric-value text-2xl mt-1 text-[#0B192C]">{v}</div></div>; }

export function StaticPage({ title, children }) {
  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline">OwnNest</div>
        <h1 className="font-serif text-5xl mt-3 text-[#0B192C]">{title}</h1>
        <div className="mt-8 text-slate-700 leading-relaxed space-y-4">{children}</div>
      </div>
    </PublicLayout>
  );
}
